import { ArrowLeft, LoaderCircle, Trash2, Upload } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, readApiError } from "../api/api-client";
import type { DeviceModelDefinition } from "../models/device-model-catalog";
import { DeviceModelPreview } from "./device-model-preview";

type DeviceModelManagerProps = {
  models: DeviceModelDefinition[];
  onCatalogChange: () => Promise<DeviceModelDefinition[]>;
  onClose: () => void;
};

export function DeviceModelManager({ models, onCatalogChange, onClose }: DeviceModelManagerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [blockPrefix, setBlockPrefix] = useState("");
  const [busy, setBusy] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>();
  const groups = useMemo(() => groupDeviceModels(models), [models]);
  const selectedModel = models.find((model) => model.id === selectedModelId) ?? models[0];

  useEffect(() => {
    if (selectedModelId && models.some((model) => model.id === selectedModelId)) {
      return;
    }
    setSelectedModelId(models[0]?.id);
  }, [models, selectedModelId]);

  const handleUpload = async (event: FormEvent) => {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError("请选择 GLB 模型文件");
      return;
    }

    setBusy(true);
    setError(undefined);
    const form = new FormData();
    form.set("blockPrefix", blockPrefix);
    form.set("displayName", displayName);
    form.set("file", file);
    try {
      const response = await apiFetch("/api/admin/device-models", {
        body: form,
        method: "POST"
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, "设备模型上传失败"));
      }
      const created = (await response.json()) as DeviceModelDefinition;
      await onCatalogChange();
      setSelectedModelId(created.id);
      setDisplayName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "设备模型上传失败");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (model: DeviceModelDefinition) => {
    if (!window.confirm(`确定删除设备型号“${model.displayName}”吗？`)) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const response = await apiFetch(`/api/admin/device-models/${encodeURIComponent(model.id)}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, "设备模型删除失败"));
      }
      await onCatalogChange();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "设备模型删除失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="device-model-manager">
      <header className="device-manager-header">
        <button className="device-manager-back" onClick={onClose} type="button">
          <ArrowLeft aria-hidden="true" size={18} />
          返回项目
        </button>
        <div>
          <span className="device-manager-eyebrow">DEVICE LIBRARY</span>
          <h1>设备模型管理</h1>
        </div>
      </header>

      <div className="device-manager-workspace">
        <aside className="device-manager-sidebar">
          <form className="device-manager-upload" onSubmit={(event) => void handleUpload(event)}>
            <div className="device-manager-section-title">
              <h2>上传设备型号</h2>
              <span>GLB · 最大 64 MB</span>
            </div>
            <label>
              <span>DXF 图块匹配前缀</span>
              <input
                disabled={busy}
                onChange={(event) => setBlockPrefix(event.target.value)}
                placeholder="例如：设备-CTU"
                value={blockPrefix}
              />
            </label>
            <p>DXF 图块名称以此值开头时命中；模型文件名不参与匹配。</p>
            <label>
              <span>设备型号名称</span>
              <input
                disabled={busy}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="显示在场景设置中的名称"
                value={displayName}
              />
            </label>
            <label>
              <span>GLB 模型文件</span>
              <input
                accept=".glb,model/gltf-binary"
                disabled={busy}
                ref={fileInputRef}
                type="file"
              />
            </label>
            <button disabled={busy || !blockPrefix.trim() || !displayName.trim()} type="submit">
              {busy ? (
                <LoaderCircle aria-hidden="true" size={18} />
              ) : (
                <Upload aria-hidden="true" size={18} />
              )}
              {busy ? "处理中" : "上传模型"}
            </button>
          </form>

          {error ? <p className="device-manager-error">{error}</p> : null}

          <section className="device-manager-list" aria-label="设备型号列表">
            <div className="device-manager-section-title">
              <h2>设备目录</h2>
              <span>{models.length} 个型号</span>
            </div>
            {groups.length === 0 ? (
              <p className="device-manager-empty">尚未上传任何设备模型。</p>
            ) : (
              groups.map(([prefix, groupModels]) => (
                <div className="device-manager-group" key={prefix}>
                  <code>{prefix}</code>
                  {groupModels.map((model, index) => (
                    <div
                      className={`device-manager-model-row${selectedModel?.id === model.id ? " is-selected" : ""}`}
                      key={model.id}
                    >
                      <button onClick={() => setSelectedModelId(model.id)} type="button">
                        <span>{model.displayName}</span>
                        {index === 0 ? <small>默认</small> : null}
                      </button>
                      <button
                        aria-label={`删除 ${model.displayName}`}
                        className="device-manager-delete"
                        disabled={busy}
                        onClick={() => void handleDelete(model)}
                        title="删除设备型号"
                        type="button"
                      >
                        <Trash2 aria-hidden="true" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ))
            )}
          </section>
        </aside>

        <section className="device-manager-preview-panel">
          <div className="device-manager-preview-heading">
            <div>
              <span className="device-manager-eyebrow">MODEL PREVIEW</span>
              <h2>{selectedModel?.displayName ?? "未选择型号"}</h2>
            </div>
            {selectedModel ? <code>{selectedModel.blockPrefix}</code> : null}
          </div>
          <DeviceModelPreview model={selectedModel} />
        </section>
      </div>
    </main>
  );
}

function groupDeviceModels(models: DeviceModelDefinition[]) {
  const groups = new Map<string, DeviceModelDefinition[]>();
  models.forEach((model) => {
    const group = groups.get(model.blockPrefix) ?? [];
    group.push(model);
    groups.set(model.blockPrefix, group);
  });
  return [...groups.entries()];
}
